import React from 'react';
import { Box, Typography } from '@mui/material';
import { NO_TASKS } from 'common/constants/firms';
import {
  fullyDilutedOwnershipFormat,
  largeCurrencyFormat,
  twoDecimalPercentFormat,
  xStandardSuffixFormat,
} from 'common/formats/formats';
import { Cells } from 'common/types/scalarSpreadsheet';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { getTotalPublishedValuationsExpression } from 'pages/Firms/hooks/useFirmSummarySheet/config/createParser/utils/getTotalPublishedValuationsExpression';
import { FUND_SUMMARY_DRAFT_CELL, SHEET_ALIASES_CONSTANTS } from 'pages/Funds/common/constants/fundSummary';
import { TaskCell } from 'pages/Funds/components';
import { getExpression, getObjectValue, handleDisabledCells } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { CellParserParams, CreateParserParams } from './types';
import {
  getAdjustedColumns,
  getAdjustedHeaders,
  getInvestmentsExpressions,
  getValuationsExpressions,
  handleTotalsCells,
  handleTotalValueCells,
} from './utils';

const {
  FUND_SUMMARY_SPREADSHEET_DIRECT_INVESTMENTS_TOTAL,
  FUND_SUMMARY_SPREADSHEET_FUND_INVESTMENTS_TOTAL,
  FUND_SUMMARY_SPREADSHEET_HEADER_TITLE,
  FUND_SUMMARY_SPREADSHEET_SUBHEADER_TITLE,
  FUND_SUMMARY_SPREADSHEET_TOTAL,
} = SHEET_ALIASES_CONSTANTS;

const DEFAULT_ALLOW_NEGATIVE_VALUE = true;
const DEFAULT_ROW_SPAN = 1;
const EXCLUDE_HEADER = 1;
const EXCLUDE_HEADER_AND_TOTAL = 2;

const HEADER_AND_TOTALS = Object.freeze([
  FUND_SUMMARY_SPREADSHEET_HEADER_TITLE,
  FUND_SUMMARY_SPREADSHEET_TOTAL,
  FUND_SUMMARY_SPREADSHEET_DIRECT_INVESTMENTS_TOTAL,
  FUND_SUMMARY_SPREADSHEET_FUND_INVESTMENTS_TOTAL,
]);

const cellParser = (params: CellParserParams) => {
  const {
    allPublishedValuationsExpression,
    alphabet,
    cellCustomClasses,
    colNumber,
    column,
    columnLegend,
    directAndFundInvestmentsTotalExpression,
    directInvestmentsExpression,
    FUND_SUMMARY_SPREADSHEET_COLUMNS,
    FUND_SUMMARY_SPREADSHEET_HEADERS,
    fundGrossIRR,
    fundInvestmentsExpression,
    fundName,
    isProcessManagementEnabled,
    key,
    measurementDateName,
    portfolioCompaniesFundGrossIRR,
    row,
    subTotalValueKey,
    tasksProgress,
    totalPublishedDirectInvestedCapitalExpression,
    totalPublishedFundInvestedCapitalExpression,
  } = params;

  const { alias, tooltipMessages = [] } = getObjectValue(row);
  const {
    disabled: isDisabled = false,
    isFundInvestment = false,
    name: companyName = '',
    slug: companySlug = '',
    value: cellValue = '',
  } = getObjectValue(column[alias]);

  // Creating cell from row and column
  const cell: ICell = { ...row, key, value: cellValue };

  // Parse cell based on Row alias
  switch (alias) {
    // Header titles
    case FUND_SUMMARY_SPREADSHEET_HEADER_TITLE:
      if (FUND_SUMMARY_SPREADSHEET_HEADERS[colNumber].defaultTooltip) {
        tooltipMessages?.push(FUND_SUMMARY_SPREADSHEET_HEADERS[colNumber].defaultTooltip);
      }

      cell.value = FUND_SUMMARY_SPREADSHEET_HEADERS[colNumber].value;
      break;

    case FUND_SUMMARY_SPREADSHEET_SUBHEADER_TITLE:
      break;

    // Totals
    case FUND_SUMMARY_SPREADSHEET_DIRECT_INVESTMENTS_TOTAL:
    case FUND_SUMMARY_SPREADSHEET_FUND_INVESTMENTS_TOTAL:
    case FUND_SUMMARY_SPREADSHEET_TOTAL:
      handleTotalsCells({
        alias,
        allPublishedValuationsExpression,
        alphabet,
        cell,
        colNumber,
        directAndFundInvestmentsTotalExpression,
        directInvestmentsExpression,
        FUND_SUMMARY_SPREADSHEET_COLUMNS,
        fundGrossIRR,
        portfolioCompaniesFundGrossIRR,
        fundInvestmentsExpression,
        subTotalValueKey,
        totalPublishedDirectInvestedCapitalExpression,
        totalPublishedFundInvestedCapitalExpression,
      });
      break;

    // Rest of the rows (excluding Header titles and Totals)
    default:
      // Parse cell based on Column number
      switch (colNumber) {
        case FUND_SUMMARY_SPREADSHEET_COLUMNS.FULLY_DILUTED_OWNERSHIP:
          cell.format = fullyDilutedOwnershipFormat;
          cell.gridType = 'percentage';
          cell.hidden = isFundInvestment;
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.INVESTED_CAPITAL:
        case FUND_SUMMARY_SPREADSHEET_COLUMNS.UNREALIZED_EQUITY:
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.REALIZED_VALUE:
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';
          cell.hidden = isFundInvestment;
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.TOTAL_VALUE:
          handleTotalValueCells({
            cell,
            isDisabled,
            isFundInvestment,
          });
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.GROSS_IRR:
          cell.format = twoDecimalPercentFormat({});
          cell.gridType = 'percentage';
          cell.hidden = isFundInvestment;
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.MOIC:
          cell.format = xStandardSuffixFormat;
          cell.gridType = 'number';
          cell.hidden = isFundInvestment;
          break;

        // Must be at the end of the switch after all conditions are evaluated
        case FUND_SUMMARY_SPREADSHEET_COLUMNS.PROCESS_MANAGEMENT:
          if (isProcessManagementEnabled) {
            cell.component = tasksProgress?.companies?.length ? (
              <TaskCell
                companyName={companyName}
                companySlug={companySlug}
                fundName={fundName}
                measurementDateName={measurementDateName}
                tasksProgress={tasksProgress}
              />
            ) : (
              <Box display="flex" justifyContent="center">
                <Typography variant="body2">{NO_TASKS}</Typography>
              </Box>
            );
            cell.forceComponent = true;
          }
          break;

        default:
          break;
      }
      break;
  }

  // Handle disabled cells (not published Valuations) and  (has only Custom Ownerships)

  const shouldCheckDisabledCell =
    !HEADER_AND_TOTALS.includes(alias) && // Ignore Header titles and Total
    (
      [
        FUND_SUMMARY_SPREADSHEET_COLUMNS.FULLY_DILUTED_OWNERSHIP, // check if has only Custom Ownerships
        FUND_SUMMARY_SPREADSHEET_COLUMNS.REALIZED_VALUE,
        FUND_SUMMARY_SPREADSHEET_COLUMNS.UNREALIZED_EQUITY,
        FUND_SUMMARY_SPREADSHEET_COLUMNS.TOTAL_VALUE,
        FUND_SUMMARY_SPREADSHEET_COLUMNS.GROSS_IRR,
        FUND_SUMMARY_SPREADSHEET_COLUMNS.MOIC,
      ] as number[]
    ).includes(colNumber);

  const includeDraftValue =
    !HEADER_AND_TOTALS.includes(alias) && // Ignore Header titles and Total
    ([FUND_SUMMARY_SPREADSHEET_COLUMNS.TOTAL_VALUE] as number[]).includes(colNumber);

  const {
    cellClassName,
    cellGridType,
    cellTooltipMessages,
    cellValue: updatedCellValue,
  } = handleDisabledCells({
    cellCustomClasses,
    colNumber,
    column,
    gridType: cell.gridType,
    row,
    shouldCheckDisabledCell,
    tooltipMessages,
    value: cell.value,
    customValue: includeDraftValue ? FUND_SUMMARY_DRAFT_CELL : '',
  });

  Object.assign(cell, {
    className: cellClassName,
    gridType: cellGridType,
    value: updatedCellValue,
    tooltipMessages: cellTooltipMessages,
  });

  // Default values
  const {
    allowEmptyValues = true,
    customKey = null,
    dbType = 'string',
    expr = '',
    format = null,
    gridType = 'string',
    readOnly = true,
    value = '',
    allowNegativeValue = DEFAULT_ALLOW_NEGATIVE_VALUE,
    rowSpan = DEFAULT_ROW_SPAN,
  } = getObjectValue(cell);

  return {
    ...cell,
    allowEmptyValues,
    allowNegativeValue,
    columnLegend,
    customKey,
    dbType,
    expr: getExpression({ expr, columnLegend }),
    format,
    gridType,
    key,
    readOnly,
    rowSpan,
    tooltipMessages,
    value,
  } as ICell;
};

const createParser = (params: CreateParserParams) => {
  const {
    areFundInvestmentsEnabled,
    cellCustomClasses,
    columns,
    companyCount,
    fundGrossIRR,
    portfolioCompaniesFundGrossIRR,
    fundInvestmentCount,
    fundName,
    isProcessManagementEnabled,
    measurementDateName,
    rowConfig,
    tasksProgress,
  } = params;

  const cells = {} as Cells;
  const alphabet = alphabetGenerator([], columns.length);

  const FUND_SUMMARY_SPREADSHEET_COLUMNS = getAdjustedColumns(isProcessManagementEnabled);
  const FUND_SUMMARY_SPREADSHEET_HEADERS = getAdjustedHeaders(isProcessManagementEnabled);

  const { allPublishedValuationsExpression, totalValueColLegend } = getValuationsExpressions({
    alphabet,
    areFundInvestmentsEnabled,
    columns,
    excludedRowsCount: EXCLUDE_HEADER_AND_TOTAL,
    FUND_SUMMARY_SPREADSHEET_COLUMNS,
  });

  const { directAndFundInvestmentsTotalExpression, directInvestmentsExpression, fundInvestmentsExpression } =
    getInvestmentsExpressions({
      areFundInvestmentsEnabled,
      companyCount,
      directInvestmentExcludedRowsCount: EXCLUDE_HEADER,
      fundInvestmentCount,
      fundInvestmentExcludedRowsCount: EXCLUDE_HEADER_AND_TOTAL,
    });

  const totalPublishedDirectInvestedCapitalExpression = getTotalPublishedValuationsExpression({
    allPublishedValuationsExpression,
    alphabet,
    currentColumn: FUND_SUMMARY_SPREADSHEET_COLUMNS.INVESTED_CAPITAL,
  });

  const totalPublishedFundInvestedCapitalExpression = getTotalPublishedValuationsExpression({
    allPublishedValuationsExpression: fundInvestmentsExpression,
    alphabet,
    currentColumn: FUND_SUMMARY_SPREADSHEET_COLUMNS.INVESTED_CAPITAL,
  });

  rowConfig.forEach((row, rowIndex: number) => {
    columns.forEach((column, colIndex: number) => {
      const rowNumber = rowIndex + 1;
      const colNumber = colIndex + 1;
      const columnLegend = alphabet[colIndex];

      const key = columnLegend + rowNumber;

      const subTotalValueKey = `${totalValueColLegend}${rowNumber}`;

      cells[key] = cellParser({
        allPublishedValuationsExpression,
        alphabet,
        cellCustomClasses,
        colNumber,
        column,
        columnLegend,
        directAndFundInvestmentsTotalExpression,
        directInvestmentsExpression,
        FUND_SUMMARY_SPREADSHEET_COLUMNS,
        FUND_SUMMARY_SPREADSHEET_HEADERS,
        fundGrossIRR,
        portfolioCompaniesFundGrossIRR,
        fundInvestmentsExpression,
        fundName,
        isProcessManagementEnabled,
        key,
        measurementDateName,
        row,
        subTotalValueKey,
        tasksProgress,
        totalPublishedDirectInvestedCapitalExpression,
        totalPublishedFundInvestedCapitalExpression,
      });
    });
  });

  return cells;
};

export default createParser;
